import React, { useMemo } from 'react';
import type { LstRotinaDoDia, LstStatus } from '../../types';
import type { TplOeeMetrics } from '../../services/lstOeeMetrics';
import { meuPadraoIsLate } from '../../utils/meuPadraoDayUtils';

export interface MeuPadraoOperationalPanelProps {
  rotinas: LstRotinaDoDia[];
  statusById: ReadonlyMap<string, LstStatus | 'pendente'>;
  oee: TplOeeMetrics | null;
  ielScore?: number | null;
}

function StatChip({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string | number;
  tone?: 'neutral' | 'blue' | 'emerald' | 'amber' | 'red' | 'violet';
}) {
  const toneCls = {
    neutral: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
    blue: 'bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-200',
    emerald: 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200',
    amber: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
    red: 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200',
    violet: 'bg-violet-50 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200',
  }[tone];

  return (
    <div className={`flex items-center gap-1.5 rounded-md px-2 py-1 ${toneCls}`}>
      <span className="text-[9px] font-semibold uppercase tracking-wide opacity-70">{label}</span>
      <span className="text-[12px] font-bold tabular-nums">{value}</span>
    </div>
  );
}

/** Indicadores de apoio — abaixo da lista, sem competir com a execução. */
export const MeuPadraoOperationalPanel = React.memo<MeuPadraoOperationalPanelProps>(
  function MeuPadraoOperationalPanel({ rotinas, statusById, oee, ielScore }) {
    const stats = useMemo(() => {
      let pendentes = 0;
      let atrasadas = 0;
      for (const r of rotinas) {
        const st = statusById.get(r.template.id) ?? r.status;
        if (st === 'pendente') pendentes += 1;
        if (meuPadraoIsLate(r, st)) atrasadas += 1;
      }
      const counts = oee?.counts;
      return {
        planejadas: rotinas.length,
        executadas: counts?.executadas ?? 0,
        pendentes,
        atrasadas,
        disciplina: oee?.disciplinaPct ?? 0,
        conformidade: oee?.conformidadePct ?? 0,
        desvios: counts?.desvio ?? 0,
      };
    }, [rotinas, statusById, oee]);

    if (rotinas.length === 0) return null;

    return (
      <section className="shrink-0 border-t border-slate-100 pt-2 dark:border-slate-800">
        <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">
          Painel operacional
        </p>
        <div className="flex flex-wrap gap-1.5">
          <StatChip label="Plan." value={stats.planejadas} />
          <StatChip label="Exec." value={stats.executadas} tone="emerald" />
          <StatChip label="Pend." value={stats.pendentes} tone="blue" />
          <StatChip label="Atras." value={stats.atrasadas} tone={stats.atrasadas > 0 ? 'red' : 'neutral'} />
          <StatChip label="Disc." value={`${stats.disciplina}%`} tone="blue" />
          <StatChip label="Conf." value={`${stats.conformidade}%`} tone="emerald" />
          <StatChip label="Desv." value={stats.desvios} tone={stats.desvios > 0 ? 'amber' : 'neutral'} />
          <StatChip label="IEL" value={ielScore != null ? ielScore : '—'} tone="violet" />
        </div>
      </section>
    );
  },
);

export default MeuPadraoOperationalPanel;
